import React, { useEffect, useState } from 'react'
import { db } from '../firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Tooltip,
  IconButton,
  Box
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function UserList({ selectedChannel, onUserSelect, userRole }) {
  const { t } = useTranslation()  // i18n Hook
  const { currentUser } = useAuth()
  const [users, setUsers] = useState([])

  useEffect(() => {
    const fetchChannelUsers = async () => {
      if (!selectedChannel) {
        setUsers([])
        return
      }

      const members = selectedChannel.members || []
      if (members.length === 0) {
        setUsers([])
        return
      }

      // Kanal dokümanında sakladığınız rol bilgileri
      const memberDetails = selectedChannel.memberDetails || []

      try {
        const usersRef = collection(db, 'users')

        // 30 değerlik limitten dolayı sorguyu parçalara bölüyoruz.
        const chunkSize = 30
        let allUserDocs = []

        for (let i = 0; i < members.length; i += chunkSize) {
          const chunk = members.slice(i, i + chunkSize)
          const q = query(usersRef, where('uid', 'in', chunk))
          const snapshot = await getDocs(q)
          allUserDocs = [...allUserDocs, ...snapshot.docs]
        }

        // Dokümanlardan kullanıcı bilgilerini çekip ek bilgilerle (role gibi) birleştiriyoruz
        let userData = allUserDocs.map((doc) => {
          const user = doc.data()
          const memberDetail = memberDetails.find((m) => m.userId === user.uid)
          return {
            id: doc.id,
            ...user,
            role: memberDetail ? memberDetail.role : 'unknown'
          }
        })

        // Eğer userRole 'participant' veya 'observer' ise, observer rolündeki kullanıcıları filtrele
        if (userRole === 'participant' || userRole === 'observer') {
          userData = userData.filter((u) => u.role !== 'observer')
        }

        setUsers(userData)
      } catch (error) {
        console.error('Kullanıcılar alınırken hata:', error)
      }
    }

    fetchChannelUsers()
  }, [selectedChannel, userRole])

  const handleClick = (user) => {
    if ((userRole === 'observer' || userRole === 'participant') && user.role !== 'moderator') {
      alert(t('userList.onlyModeratorsMessage'))
      return
    }
    if (onUserSelect) {
      onUserSelect(user)
    }
  }

  const getUserRoleLabel = (role) => {
    switch (role) {
      case 'moderator':
        return t('userList.moderatorLabel')
      case 'participant':
        return t('userList.participantLabel')
      case 'observer':
        return t('userList.observerLabel')
      default:
        return t('userList.unknownRole')
    }
  }

  if (!selectedChannel) {
    return <Typography>{t('userList.selectChannel')}</Typography>
  }

  if (users.length === 0) {
    return <Typography>{t('userList.noUsers')}</Typography>
  }

  return (
    <List dense>
      {users.map((user) => {
        const isDisabled =
          (userRole === 'observer' || userRole === 'participant') && user.role !== 'moderator'

        const isCurrentUser = user.uid === currentUser.uid
        const canSendMessage = !isCurrentUser && !isDisabled

        return (
          <ListItem
            key={user.id}
            disableGutters
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              px: 1.5
            }}
          >
            <ListItemAvatar>
              {user.avatarUrl ? (
                <Avatar alt={user.nickname} src={user.avatarUrl} />
              ) : (
                <Avatar>
                  {user.nickname ? user.nickname.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              )}
            </ListItemAvatar>
            <ListItemText
              primary={user.nickname || user.email}
              secondary={getUserRoleLabel(user.role)}
              sx={{ ml: -1, flex: 1 }}
            />
            <Box>
              {canSendMessage && (
                <Tooltip title={t('userList.sendMessageTitle')}>
                  <IconButton
                    size="small"
                    onClick={() => handleClick(user)}
                    sx={{ color: 'primary.main' }}
                    aria-label={`${t('userList.sendMessageTitle')} ${user.nickname || user.email}`}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </ListItem>
        )
      })}
    </List>
  )
}